// api.js (Updated: Handle resCode=2 in searchCarplate like resCode=0, force IsFree=true if resCode=2)
import axios from 'axios';
import CryptoJS from 'crypto-js';
import config from '../src/Config';
import authHeader from './authHeader';
const API_BASE_URL = config.api.baseUrl;

const ENCRYPTION_KEY = '12345';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout from 10000ms to 30000ms to handle potential slow backend responses
  validateStatus: (status) => status < 500 // FIX: Treat 400 as success (enter try block) so we can handle resCode=1 in try; only 5xx go to catch
});

// Add request interceptor to include dynamic headers
api.interceptors.request.use(
  (config) => {
    console.log('Request interceptor triggered for URL:', config.url);
    // Skip adding auth headers for login endpoint to avoid conflicts pre-auth
    if (config.url.includes('Login')) {
      console.log('Skipping auth headers for Login endpoint');
      return config;
    }
    const headers = authHeader();
    console.log('Adding auth headers:', headers);
    config.headers = { ...config.headers, ...headers };
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
api.interceptors.response.use(
  (response) => {
    console.log('Response received for URL:', response.config.url, 'Status:', response.status, 'Data:', response.data);
    return response;
  },
  (error) => {
    // Since validateStatus handles <500 as success, this only catches >=500
    console.error('Server error (500+) for URL:', error.config?.url, 'Status:', error.response?.status, 'Error:', error.message);
    return Promise.reject(error);
  }
);

const handleError = (error, customMessage) => {
  console.error('handleError called with message:', customMessage, 'Full error:', error);
  throw new Error(customMessage);
};

export const searchCarplate = async (carplate, guid) => {
  console.log('searchCarplate called with carplate:', carplate, 'guid:', guid, '(支援中文車牌 e.g. 粵B1Q15N / 澳55678)');
  if (!carplate || typeof carplate !== 'string') {
    const err = '車牌號碼無效 / Invalid carplate';
    console.error(err);
    throw new Error(err);
  }
  if (!guid || typeof guid !== 'string') {
    const err = 'GUID無效 / Invalid GUID';
    console.error(err);
    throw new Error(err);
  }

  try {
    const device = 'app';
    const payload = {
      lpn: carplate, // 支持中文 lpn (e.g. 澳55678)
      device: device
    };
    console.log('Sending POST to /GetLpnInfo with payload:', payload, 'and GUID via headers:', guid);

    const response = await api.post('/GetLpnInfo', payload);
    console.log('Response status:', response.status, 'responseData:', response.data);
    const responseData = response.data;
    const resCode = Number(responseData.resCode || 0);
    const resmsg = responseData.resmsg || '';
    const data = responseData.data || null;

    console.log('Raw response from /GetLpnInfo (status:', response.status, '):', { resCode, resmsg, data });

    let processedData = null;
    if (resCode === 0 || resCode === 2) { // UPDATED: Handle resCode=2 same as 0, but force IsFree=true
      // 驗證必要字段
      if (!data || !data.lpn || !data.entryTime) {
        const err = '無效的查詢結果 / Invalid query result';
        console.error(err, 'Missing fields:', { lpn: data?.lpn, entryTime: data?.entryTime });
        throw new Error(err);
      }

      // 設置是否需要付款 (force true for resCode=2)
      const isFree = (resCode === 2) || (data.fee === 0);
      console.log('Processed response - isFree:', isFree, '(resCode:', resCode, ')');

      processedData = {
        Carplate: data.lpn,
        CarparkFee: data.fee || 0, // Ensure 0 for resCode=2
        ParkingFee: data.parkingfee || 0,
        EvCharge: data.evcharge || 0,
        EntryTime: data.entryTime,
        ExitTime: data.exitTime || '',
        LpnPhotoPath: data.lpnPhotoPath || '',
        DurationMinutes: data.durationMinutes || 0,
        RefundValue: data.refundValue || 0,
        SDticketNo: data.sDticketNo || '',
        ResMsg: resmsg || (isFree ? 'Free of charge' : 'Fee required'),
        IsFree: isFree // 新增字段，標記是否免費
      };
      console.log('Returning processed response for resCode=', resCode, ':', processedData);
    } else {
      console.log('Business error response for resCode:', resCode, 'resmsg:', resmsg);
    }

    // Always return the structure for component to handle
    return {
      resCode: resCode,
      resmsg: resmsg,
      data: processedData
    };
  } catch (error) {
    // Now only catches network/5xx errors, not business errors
    console.error('searchCarplate error (network/5xx):', error);
    let errorMessage = error.message || '查詢失敗，系統錯誤，請重新嘗試 / Query failed, system error, please try again';
    console.error('searchCarplate final error message (to be thrown):', errorMessage);
    console.log('This error will be caught by Enquiry.js handleSearch to setError');
    throw new Error(errorMessage);
  }
};

export const createOrder = async (carplate, fee, entrytime, guid) => {
  // 驗證參數
  console.log('[createOrder] Input parameters:', { carplate, fee, entrytime, guid });
  if (!carplate || typeof carplate !== 'string') {
    const err = '車牌號碼無效 / Invalid carplate';
    console.error(err);
    throw new Error(err);
  }
  if (!fee || isNaN(fee) || fee <= 0) {
    const err = '費用無效 / Invalid fee';
    console.error(err);
    throw new Error(err);
  }
  if (!entrytime || typeof entrytime !== 'string') {
    const err = '入場時間無效 / Invalid entrytime';
    console.error(err);
    throw new Error(err);
  }
  if (!guid || typeof guid !== 'string') {
    const err = 'GUID無效 / Invalid GUID';
    console.error(err);
    throw new Error(err);
  }

  try {
    // 加密數據
    const dataToEncrypt = JSON.stringify({ carplate, fee, entrytime });
    console.log('[createOrder] Data to encrypt:', dataToEncrypt);
    const encryptedData = CryptoJS.AES.encrypt(dataToEncrypt, ENCRYPTION_KEY).toString();
    const encodedEncryptedData = encodeURIComponent(encryptedData);
    const redirectUrl = `${config.api.redirectBaseUrl}/thankyou?encryptedData=${encodedEncryptedData}`;
    console.log('[createOrder] Redirect URL:', redirectUrl);

    // 發送請求
    const payload = {
      currency: 'HKD',
      hppConfig: {
        lang: 'zh',
        quickPay: true,
        redirect: redirectUrl,
        shopper: carplate,
        allowAppRedirect: true,
        forceDefaultEmail:true
      },
      products: [
        {
          name: '停車場費用',
          price: Number(fee.toFixed(2)),
          quantity: 1,
        },
      ],
      total: Number(fee.toFixed(2)),
    };
    console.log('[createOrder] Sending request with payload:', payload);

    // Remove manual Authorization header; rely on interceptor/authHeader (which uses context GUID post-login)
    const response = await api.post('/create-order', payload);
    console.log('[createOrder] Response received:', response.data);

    return response.data;
  } catch (error) {
    console.error('[createOrder] Full error details:', error); // Added more detailed logging for debugging
    // 提取後端錯誤訊息（如果存在）
    let errorMessage = '創建訂單失敗 / Failed to create order';
    if (error.code === 'ECONNABORTED') {
      errorMessage = '請求超時，請檢查網路連線並重試 / Request timeout, please check network and try again';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    console.error('[createOrder] Final error message:', errorMessage);
    handleError(error, errorMessage);
  }
};

export const checkOrderStatus = async (orderId, guid) => {
  console.log('checkOrderStatus called with orderId:', orderId, 'guid:', guid);
  if (!orderId) {
    const err = '訂單ID無效 / Invalid order ID';
    console.error(err);
    throw new Error(err);
  }
  if (!guid || typeof guid !== 'string') {
    const err = 'GUID無效 / Invalid GUID';
    console.error(err);
    throw new Error(err);
  }

  try {
    // Remove manual Authorization header; rely on interceptor/authHeader (which uses context GUID post-login)
    const response = await api.get(`${config.api.endpoints.checkOrderStatus}/${orderId}`);
    console.log('checkOrderStatus response:', response.data);
    return response.data;
  } catch (error) {
    console.error('checkOrderStatus error:', error);
    handleError(error, '查詢訂單狀態失敗 / Failed to check order status');
  }
};

export const login = async (guid) => {
  console.log('login called with guid:', guid);
  if (!guid) {
    const err = 'GUID無效 / Invalid GUID';
    console.error(err);
    throw new Error(err);
  }

  try {
    const loginUrl = `/Login/${guid}`; // 改成 path parameter
    console.log('Sending GET to', loginUrl);
    const response = await api.get(loginUrl);
    console.log('login response:', response.data);
    return response.data;
  } catch (error) {
    console.error('login error:', error);
    handleError(error, '登入失敗 / Failed to login');
  }
};